import { HTMLAttributes, useEffect } from 'react'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Media from '@/components/base/media'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { useLoaderStore } from '@/store/loader'

import { useHeroMotion } from './motion'
import { cn } from '@/lib/utils'
import { WPMedia } from '@/types'

export type HeroProps = HTMLAttributes<HTMLElement> &
  Partial<{
    pretitle: string
    title: string
    text: string
    media: WPMedia
  }>

const HomeHero = ({ pretitle, title, text, media, className, ...props }: HeroProps) => {
  const { motionRef } = useHeroMotion()
  const setLoaderState = useLoaderStore((state) => state.setState)

  const handleLoad = () => {
    setLoaderState({ pageLoaded: true, loaded: true })
  }

  useEffect(() => {
    if (
      !media?.poster?.url &&
      !media?.video?.url &&
      !media?.poster_mobile?.url &&
      !media?.video_mobile?.url
    ) {
      setLoaderState({ pageLoaded: true, loaded: true })
    }
  }, [media, setLoaderState])

  return (
    <Section ref={motionRef} className={cn('relative', className)} {...props}>
      <div className="grid h-screen w-full">
        <div
          className={cn('col-[1/-1] row-[1/-1]', 'relative overflow-clip')}
          data-motion-media-mask
        >
          {(media?.poster?.url || media?.video?.url) && (
            <div className="-z-1 absolute left-0 top-0 h-full w-full bg-text-accent-light">
              {/* <div className="absolute inset-0 z-10 bg-[black]/20" /> */}
              <Media onLoad={handleLoad} media={media} data-motion-media />
            </div>
          )}
        </div>

        <Container className={cn('col-[1/-1] row-[1/-1]', 'h-svh-vh relative')}>
          <Grid className="-mt-spacing-sm h-full items-center">
            <div
              className={cn(
                'col-span-2 md:col-span-6 lg:col-span-8 lg:col-start-3',
                'grid gap-y-spacing-sm md:gap-y-spacing-lg'
              )}
            >
              {pretitle ? (
                <div className="flex md:mx-auto">
                  <Typography
                    variant="body"
                    className={cn(
                      'supports-ios:text-white-light text-text-primary-light',
                      'supports-ios:mix-blend-normal mix-blend-overlay'
                    )}
                    data-motion-pretitle
                  >
                    {pretitle}
                  </Typography>
                </div>
              ) : null}

              {title ? (
                <div className="flex md:mx-auto">
                  <Typography
                    variant="h4"
                    component="h1"
                    className={cn(
                      'supports-ios:text-white-light text-text-heading-invert-light',
                      'supports-ios:mix-blend-normal mix-blend-overlay',
                      'md:text-center'
                    )}
                    data-motion-title
                  >
                    {title}
                  </Typography>
                </div>
              ) : null}

              {text ? (
                <div className="flex text-white-light md:text-center">
                  <Typography variant="body" className="grow" data-motion-paragraph>
                    {text}
                  </Typography>
                </div>
              ) : null}
            </div>
          </Grid>
        </Container>
      </div>
    </Section>
  )
}

export default HomeHero
